import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import fr from '../locales/fr.json';
import de from '../locales/de.json';

type Language = 'fr' | 'de';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = { fr, de };

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [language, setLanguageState] = useState<Language>(() => {
    // Priorité 1: URL (/fr/ ou /de/)
    const pathLang = location.pathname.split('/')[1];
    if (pathLang === 'fr' || pathLang === 'de') {
      return pathLang;
    }
    // Priorité 2: localStorage
    const saved = localStorage.getItem('grenzexpert-language');
    return (saved === 'de' ? 'de' : 'fr') as Language;
  });

  useEffect(() => {
    localStorage.setItem('grenzexpert-language', language);
    
    // Synchroniser l'URL avec la langue
    const currentPath = location.pathname;
    const pathLang = currentPath.split('/')[1];
    
    // Si l'URL n'a pas de préfixe de langue ou a un mauvais préfixe
    if (pathLang !== language) {
      let newPath = currentPath;
      
      // Retirer l'ancien préfixe de langue si présent
      if (pathLang === 'fr' || pathLang === 'de') {
        newPath = currentPath.substring(3) || '/';
      }
      
      // Ajouter le nouveau préfixe de langue
      newPath = `/${language}${newPath === '/' ? '' : newPath}`;
      
      navigate(newPath, { replace: true });
    }
  }, [language, location.pathname, navigate]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): any => {
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
